'use client';

import { ToastContainer, Toast } from 'react-bootstrap';
import { ReactNode, useState, createContext, useContext, useCallback } from 'react';

import * as R from 'remeda';
import { getCauseChain, noop } from '@/utils';

export interface ToastData {
  id?: string;
  header?: string;
  body?: string;
  error?: Error | string | unknown;
  variant?: string;
  autoHideDelay?: number;
}

interface FullToastData extends ToastData {
  id: string;
}

export interface ToastState {
  id: string;
  close: () => void;
}

interface IToastContext {
  allToastData: FullToastData[];
  closeToast: (id: string) => void;
  addToast: (data: ToastData | string, options?: ToastData) => ToastState;
  errorToast: (error: Error | string | any, options?: ToastData) => void;
}

export const ToastContext = createContext<IToastContext>({
  allToastData: [],
  closeToast: noop,
  addToast: () => ({ id: '', close: noop }),
  errorToast: noop,
});

export const useToast = () => useContext(ToastContext);

export function ToastContextWrapper({
  children,
  manualContainer = false,
}: {
  children: ReactNode;
  manualContainer?: boolean;
}) {
  const [allToastData, setAllToastData] = useState<FullToastData[]>([]);
  const closeToast = useCallback(
    (id: string) => setAllToastData((prev) => prev.filter((toast) => toast.id !== id)),
    []
  );
  const addToast = useCallback(
    (data: ToastData | string, options?: ToastData) => {
      let config: ToastData = R.isString(data) ? { body: data } : data;

      // Merge options object with data object if it's provided
      if (options && !R.isEmpty(options)) {
        config = { ...options, ...config };
      }

      if (config.error) {
        console.error(config.error);
        config.body = getCauseChain(config.error);
        config.variant = 'danger';
      }
      config.autoHideDelay ??= 5000;
      const id = `toast_${Date.now().toString()}`;
      const toastData: FullToastData = {
        id,
        ...config,
      };
      setAllToastData((prev) => {
        if (prev.find((toast) => toast.body === toastData.body)) {
          console.warn('Skipping duplicate toast', toastData);
          return prev;
        }
        return [...prev, toastData];
      });
      return {
        id,
        close: () => closeToast(id),
      };
    },
    [closeToast]
  );

  const errorToast = useCallback(
    (error: Error | string | unknown, options?: ToastData) => addToast({ error }, options),
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ allToastData, closeToast, addToast, errorToast }}>
      {!manualContainer && <ToastsContainer />}
      {children}
    </ToastContext.Provider>
  );
}

export function ToastsContainer() {
  const { allToastData, closeToast } = useContext(ToastContext);

  const toasts = allToastData.map(({ id, header, body, variant, autoHideDelay = 0 }) => {
    // Autoclose if autoHideDelay is a positive number of milliseconds
    const autoHide = Boolean(autoHideDelay > 0);
    const delay = autoHide ? autoHideDelay : 0;

    return (
      <Toast
        key={id}
        onClose={() => closeToast(id)}
        bg={variant || 'info'}
        autohide={autoHide}
        delay={delay}
      >
        <Toast.Header>{header}</Toast.Header>
        <Toast.Body>{body}</Toast.Body>
      </Toast>
    );
  });

  return <ToastContainer position="top-end">{toasts}</ToastContainer>;
}
